/**
 * "Editar dia" — título, destino, tema e cor (teste físico, item 28).
 *
 * O tema é uma lista fechada e genérica (Parques, Compras, Show…); a cor é
 * uma de oito. Escolher o tema sugere a cor dele; a pessoa pode trocar. É o
 * que pinta a faixa do dia no Roteiro e o pino da categoria.
 */
import { DAY_CATEGORIES, DAY_CATEGORY_LABELS, DAY_COLORS, dayColorOf, type DayCategory, type DayColor } from '@nv/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { type DiaDaViagem, updateTripDay } from '../../trip/dias';
import { Sheet } from '../../ui/Sheet';
import { Field } from '../auth/Field';

export const NOME_DA_COR: Record<DayColor, string> = {
  coral: 'Coral',
  laranja: 'Laranja',
  amarelo: 'Amarelo',
  verde: 'Verde',
  azul: 'Azul',
  violeta: 'Violeta',
  rosa: 'Rosa',
  cinza: 'Cinza',
};

export function EditarDiaSheet({ tripId, dia, onFechar }: { tripId: string; dia: DiaDaViagem; onFechar: () => void }) {
  const cliente = useQueryClient();
  const [titulo, setTitulo] = useState(dia.title ?? '');
  const [destino, setDestino] = useState(dia.destination ?? '');
  const [categoria, setCategoria] = useState<DayCategory | null>(dia.category ?? null);
  const [cor, setCor] = useState<DayColor | null>(dia.color ?? null);
  const corEfetiva = dayColorOf(categoria, cor);

  const salvar = useMutation({
    mutationFn: () => updateTripDay(tripId, dia.id, { title: titulo.trim() || null, destination: destino.trim() || null, category: categoria, color: cor }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['viagem', 'dias', tripId] });
      onFechar();
    },
  });

  return (
    <Sheet titleId="editar-dia-titulo" onFechar={onFechar} testId="editar-dia">
      <h2 className="sheet__title" id="editar-dia-titulo">Editar dia</h2>
      <Field label="Título do dia" value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Ex.: Dia de parque" maxLength={80} />
      <Field label="Onde" value={destino} onChange={(e) => setDestino(e.target.value)} placeholder="Bairro, cidade ou lugar" maxLength={120} />

      <span className="nv-section-label">Tema do dia</span>
      <div className="fin-nova__chips" data-testid="dia-temas">
        <button type="button" className={categoria === null ? 'fin-chip fin-chip--ativo' : 'fin-chip'} onClick={() => setCategoria(null)}>
          Sem tema
        </button>
        {DAY_CATEGORIES.map((c) => (
          <button
            type="button"
            key={c}
            className={categoria === c ? 'fin-chip fin-chip--ativo' : 'fin-chip'}
            onClick={() => {
              setCategoria(c);
              // Trocar o tema sugere a cor dele, a menos que a pessoa já tenha escolhido uma.
              if (cor === null || (categoria && cor === DAY_CATEGORY_LABELS[categoria].cor)) setCor(DAY_CATEGORY_LABELS[c].cor);
            }}
            data-testid={`dia-tema-${c}`}
          >
            {DAY_CATEGORY_LABELS[c].rotulo}
          </button>
        ))}
      </div>

      {/* O bom dia do grupo (8h) monta a frase a partir do tema e do título do dia. */}
      <p className="auth-note">O bom dia do grupo, às 8h, usa o tema e o título do dia.</p>

      <span className="nv-section-label">Cor</span>
      <div className="dia-cores" role="radiogroup" aria-label="Cor do dia" data-testid="dia-cores">
        {DAY_COLORS.map((c) => (
          <button
            type="button"
            key={c}
            role="radio"
            aria-checked={corEfetiva === c}
            aria-label={NOME_DA_COR[c]}
            className={`dia-cor dia-cor--${c}${corEfetiva === c ? ' dia-cor--ativa' : ''}`}
            onClick={() => setCor(c)}
            data-testid={`dia-cor-${c}`}
          />
        ))}
      </div>

      {salvar.isError && (
        <p className="auth-error" role="alert">
          {(salvar.error as Error).message}
        </p>
      )}
      <button type="button" className="nv-btn nv-btn--primary nv-btn--block" disabled={salvar.isPending} onClick={() => salvar.mutate()} data-testid="editar-dia-salvar">
        {salvar.isPending ? 'Salvando…' : 'Salvar dia'}
      </button>
    </Sheet>
  );
}
